if (!customElements.get('mp-technology')) {
  customElements.define('mp-technology', class extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._nav = this.querySelector('[data-tech-nav]');
      this._entries = Array.from(this._nav?.querySelectorAll('a[href]') || [])
        .map(link => ({ link, target: this._targetForLink(link) }))
        .filter(entry => entry.target?.matches('[data-tech-section]'));
      if (!this._nav || !this._entries.length) return;
      this._controller = new AbortController();
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this._controller.signal };
      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('a[href]');
        if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
        const target = this._targetForLink(link);
        if (!target || (target !== this && !target.matches('[data-tech-section]'))) return;
        event.preventDefault();
        const url = new URL(link.getAttribute('href'), location.href);
        if (url.hash !== location.hash) {
          const state = history.state && typeof history.state === 'object' ? { ...history.state } : {};
          delete state.scrollTop;
          history.pushState(state, '', url.hash);
        }
        this._scrollTo(target, true);
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }, options);
      document.addEventListener('scroll', () => this._queueUpdate(), { ...options, capture: true, passive: true });
      window.addEventListener('resize', () => this._measure(), options);
      window.addEventListener('hashchange', () => this._restoreHash(), options);
      window.addEventListener('popstate', () => this._restoreHash(), options);
      window.addEventListener('pageshow', event => {
        if (!event.persisted) this._restoreHash();
        this._queueUpdate();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!(event.target instanceof Element) || !this.contains(event.target)) return;
        const details = event.target.closest('.mp-tech-disclosure');
        if (details) details.open = true;
        const target = event.target.closest('[data-tech-section]');
        if (target) this._scrollTo(target, false);
      }, options);
      this.addEventListener('toggle', () => this._queueUpdate(), { ...options, capture: true });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target === this || event.target.contains?.(this)) this.disconnectedCallback();
      }, options);
      this._resize = new ResizeObserver(() => this._measure());
      this._resize.observe(this._nav);
      const header = document.querySelector('[data-mp-header-bar]');
      if (header) this._resize.observe(header);
      this._entries.forEach(entry => this._resize.observe(entry.target));
      this._measure();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._restoreHash(), { ...options, once: true });
      } else this._restoreHash();
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      this._resize?.disconnect();
      cancelAnimationFrame(this._frame);
      cancelAnimationFrame(this._hashFrame);
      this._frame = null;
      this._hashFrame = null;
    }

    _targetForLink(link) {
      try {
        const url = new URL(link.getAttribute('href'), location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return null;
        const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        return target === this || this.contains(target) ? target : null;
      } catch { return null; }
    }

    _measure() {
      if (!this.isConnected || !this._nav) return;
      const height = Math.ceil(this._nav.getBoundingClientRect().height);
      if (height !== this._navHeight) {
        this._navHeight = height;
        this.style.setProperty('--tech-nav-height', `${height}px`);
      }
      this._queueUpdate();
    }

    _headerHeight() {
      const header = document.querySelector('[data-mp-header-bar]');
      if (header) return Math.ceil(header.getBoundingClientRect().height);
      return parseFloat(getComputedStyle(this).getPropertyValue('--mp-header-height')) || 0;
    }

    _scrollOwner(target) {
      for (let parent = target.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        if (/(auto|scroll|overlay)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight + 1) return parent;
      }
      return window;
    }

    _scrollTo(target, smooth) {
      if (!target?.isConnected) return;
      const owner = this._scrollOwner(target);
      const ownerTop = owner === window ? 0 : owner.getBoundingClientRect().top + owner.clientTop;
      const current = owner === window ? window.scrollY : owner.scrollTop;
      const offset = this._headerHeight() + (target === this ? 0 : this._navHeight || 0) + 20;
      owner.scrollTo({ top: Math.max(0, current + target.getBoundingClientRect().top - ownerTop - offset), behavior: smooth && !this._motion.matches ? 'smooth' : 'instant' });
      this._queueUpdate();
    }

    _restoreHash() {
      cancelAnimationFrame(this._hashFrame);
      this._hashFrame = requestAnimationFrame(() => {
        this._hashFrame = null;
        if (!this.isConnected || Number.isFinite(history.state?.scrollTop)) return;
        let id;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const target = id === this.id ? this : this._entries.find(entry => entry.target.id === id)?.target;
        if (target) this._scrollTo(target, false);
        this._queueUpdate();
      });
    }

    _queueUpdate() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        if (!this.isConnected || !this._entries?.length) return;
        const entries = this._entries.filter(entry => entry.target.isConnected && entry.target.getClientRects().length);
        if (!entries.length) return;
        const first = entries[0];
        const owner = this._scrollOwner(first.target);
        const ownerTop = owner === window ? 0 : owner.getBoundingClientRect().top + owner.clientTop;
        const threshold = ownerTop + this._headerHeight() + (this._navHeight || 0) + 24;
        const currentScroll = owner === window ? window.scrollY : owner.scrollTop;
        const viewport = owner === window ? window.innerHeight : owner.clientHeight;
        const extent = owner === window ? document.scrollingElement.scrollHeight : owner.scrollHeight;
        const atBottom = extent > viewport + 1 && currentScroll + viewport >= extent - 2;
        const active = atBottom ? entries.at(-1) : entries.filter(entry => entry.target.getBoundingClientRect().top <= threshold).at(-1) || first;
        if (this._active === active) return;
        this._active = active;
        this._entries.forEach(entry => {
          if (entry === active) entry.link.setAttribute('aria-current', 'location');
          else entry.link.removeAttribute('aria-current');
        });
        if (this._nav.scrollWidth > this._nav.clientWidth + 1) {
          const linkBounds = active.link.getBoundingClientRect();
          const navBounds = this._nav.getBoundingClientRect();
          const left = this._nav.scrollLeft + linkBounds.left - navBounds.left - (this._nav.clientWidth - linkBounds.width) / 2;
          this._nav.scrollTo({ left: Math.max(0, left), behavior: 'instant' });
        }
      });
    }
  });
}
